import React, {useCallback, useEffect, useMemo, useState} from "react"
import {Card, Col, DatePicker, Descriptions, Row} from "antd"
import {Helmet} from "react-helmet-async"
import {useDispatch, useSelector} from "react-redux"
import {NavLink, useHistory, useLocation, useParams} from "react-router-dom"
import {StateType} from "../../store/store"
import {BackButton} from "../../components/BackButton/BackButton"
import {Loading} from "../../components/Loading/Loading"
import moment from "moment/moment"
import {getPSLeakDeviceIndication, setPSLeakDeviceIndication, setWSPSLeakDeviceIndication} from "../../store/psLeakDeviceIndicationReducer"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import "dayjs/locale/ru"
import locale from "antd/es/date-picker/locale/ru_RU"
import {psLeakDeviceIndicationApi} from "../../api/psLeakDeviceIndicationApi"
import {catchError} from "../../store/sliceHelper"
import {Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {filterToString} from "../../helps/filterToString"
import {PSLeakDeviceIndicationsResponse} from "../../models/response"
import {getTimeZoneDiffString} from "../../helps/timezoneDiff"

dayjs.extend(customParseFormat)

export const PSLeakDeviceIndication = () =>
{
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const id = parseInt(useParams<{ id: string }>()?.id, 10)
    const psLeakDeviceIndication = useSelector((state: StateType) => state.psLeakDeviceIndicationReducer.psLeakDeviceIndication)
    const ws = useSelector((state: StateType) => state.psLeakDeviceIndicationReducer.ws)
    const [graph, setGraph] = useState<PSLeakDeviceIndicationsResponse | null>(null)
    const [filter, setFilter] = useState<{ dateFrom?: string, dateTo?: string }>({})
    const controller = useMemo(() => new AbortController(), [id])

    useEffect(() =>
    {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter))
        {
            setFilter({...filterString})
        }
        else if (location.search !== filterToString(filter))
        {
            history.replace({search: filterToString(filter)})
        }
    }, [filter, history, location])

    useEffect(() =>
    {
        if (id)
        {
            dispatch(getPSLeakDeviceIndication({id, controller}))
        }

        return () =>
        {
            controller.abort()
            dispatch(setPSLeakDeviceIndication(null))
        }
    }, [dispatch, id, controller])

    const getGraph = useCallback(async () =>
    {
        if (psLeakDeviceIndication && location)
        {
            try
            {
                setGraph(null)

                const filter = filterFromString(location.search)

                const res = await psLeakDeviceIndicationApi.getIndicationGraph({
                    idDevice: psLeakDeviceIndication.idDevice, name: [psLeakDeviceIndication.name], dateFrom: filter?.dateFrom, dateTo: filter?.dateTo, controller,
                })

                if (res.data) setGraph(res.data)
            }
            catch (e: any)
            {
                catchError(e, dispatch)
            }

        }
    }, [dispatch, psLeakDeviceIndication, location, controller])

    useEffect(() =>
    {
        getGraph()
    }, [getGraph])

    useEffect(() =>
    {
        if (ws && ws.id === id)
        {
            dispatch(getPSLeakDeviceIndication({id, controller, withoutLoadingIndicator: true}))
        }
        dispatch(setWSPSLeakDeviceIndication(null))
    }, [dispatch, ws, id, controller])

    const graphMap = useMemo(() =>
    {
        const bars: Array<string> = []
        const data: Array<any> = []

        graph?.data?.forEach(i =>
        {
            if (!bars.includes(i.name)) bars.push(i.name)
            data.push({date: `${moment(i.date).format('DD.MM.YYYY HH:mm')} ${getTimeZoneDiffString(i.timeZone)}`, [i.name]: i.value})
        })

        if (graph?.total === 0) return <div style={{textAlign: "center", margin: 20, fontSize: 16}}>Нет данных</div>

        return <div style={{height: 500}}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart width={1000} height={500} data={data}>
                    <CartesianGrid strokeDasharray="3 3"/>
                    <XAxis dataKey="date"/>
                    <YAxis/>
                    <Tooltip/>
                    <Legend/>
                    {bars.map(i => <Bar key={i} dataKey={i} fill={"#8884d8"}/>)}
                </BarChart>
            </ResponsiveContainer>
        </div>
    }, [graph])

    if (!psLeakDeviceIndication) return <Loading/>

    return (<div className="description-page">
        <Helmet>
            <title>{`Показания ${psLeakDeviceIndication?.name}`}</title>
        </Helmet>
        <div className="description-header">
            <BackButton onClick={() => history.goBack()}/>
        </div>

        <Descriptions bordered column={{
            xs: 1, sm: 1, md: 1, lg: 2,
        }}>
            <Descriptions.Item label="Название">{psLeakDeviceIndication?.name}</Descriptions.Item>
            <Descriptions.Item label="ID">{psLeakDeviceIndication?.id}</Descriptions.Item>
            <Descriptions.Item label="ID локальный">{psLeakDeviceIndication?.idLocal}</Descriptions.Item>
            <Descriptions.Item label="Оборудование">
                <NavLink to={`/psLeakDevice/${psLeakDeviceIndication.idDevice}`}>{psLeakDeviceIndication.nameDevice}</NavLink>
            </Descriptions.Item>
            <Descriptions.Item label="Объект">
                <NavLink to={`/object/${psLeakDeviceIndication.idObject}`}>{psLeakDeviceIndication.nameObject}</NavLink>
            </Descriptions.Item>
            <Descriptions.Item label="Группа">
                <NavLink to={`/objectGroup/${psLeakDeviceIndication.idObjectGroup}`}>{psLeakDeviceIndication.nameObjectGroup}</NavLink>
            </Descriptions.Item>
            <Descriptions.Item label="Компания">
                <NavLink to={`/company/${psLeakDeviceIndication.idCompany}`}>{psLeakDeviceIndication.nameCompany}</NavLink>
            </Descriptions.Item>
            <Descriptions.Item label="Дата">{`${moment(psLeakDeviceIndication.date).format('DD.MM.YYYY HH:mm')} ${getTimeZoneDiffString(psLeakDeviceIndication.timeZone)}`}</Descriptions.Item>
            <Descriptions.Item label="Значение">{psLeakDeviceIndication?.value}</Descriptions.Item>
            <Descriptions.Item label="Ед. измерения">{psLeakDeviceIndication?.units}</Descriptions.Item>
        </Descriptions>

        <div className="tabled-side">
            <Row gutter={[24, 0]}>
                <Col xs={24}>
                    <Card variant="borderless"
                          className="criclebox tablespace mb-24"
                          title={<div className="table-title">
                              <div>График</div>
                          </div>}
                          extra={<div className="table-title-extra">
                              <DatePicker locale={locale}
                                          showTime
                                          placeholder="Дата от"
                                          format="DD.MM.YYYY HH:mm"
                                          showSecond={false}
                                          style={{height: 50, width: 150}}
                                          value={filter.dateFrom ? dayjs(filter.dateFrom) : null}
                                          onChange={(value) =>
                                          {
                                              setFilter({...filter, dateFrom: undefined})
                                              history.replace({search: filterToString({...filter, dateFrom: value ? value.format("YYYY-MM-DD HH:mm") : null, offset: 0})})
                                          }}
                                          disabledDate={(currentDate) => currentDate > dayjs(new Date())}
                              />

                              <DatePicker locale={locale}
                                          showTime
                                          placeholder="Дата по"
                                          format="DD.MM.YYYY HH:mm"
                                          showSecond={false}
                                          style={{height: 50, width: 150}}
                                          value={filter.dateTo ? dayjs(filter.dateTo) : null}
                                          onChange={(value) =>
                                          {
                                              setFilter({...filter, dateTo: undefined})
                                              history.replace({search: filterToString({...filter, dateTo: value ? value.format("YYYY-MM-DD HH:mm") : null, offset: 0})})
                                          }}
                                          disabledDate={(currentDate) => filter.dateFrom ? currentDate < dayjs(filter.dateFrom) || currentDate > dayjs(new Date()) : currentDate > dayjs(
                                              new Date())}
                              />
                          </div>}
                    >
                        <div>
                            {graph ? graphMap : <Loading/>}
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    </div>)
}